import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/library/accordion";
import { GraduationCap, Layers, IndianRupee, Wallet } from "lucide-react";
import { useAcademyChildModuleData } from "@/module/academy/helpers/useAcademyChildModuleData";
import useProgramVariants from "@/features/academy/api/useProgramVariants";
import { useMemo } from "react";

const fmt = (v: number) =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 0 });

function InfoField({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl bg-[#f5f7f6] px-3 py-2.5">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-white">
        <Icon size={14} className="text-[#505e59]" />
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#8a918d]">
          {label}
        </span>
        <span className="text-[13px] font-medium text-[#1e302a] tracking-[-0.26px] truncate">
          {value || "—"}
        </span>
      </div>
    </div>
  );
}

export default function AcademyChoiceInfo({ child }: any) {
  const { variantName, variantId } = useAcademyChildModuleData(child);
  const { variants } = useProgramVariants();

  const variant = useMemo(
    () => variants?.find((v: any) => v.id === variantId),
    [variants, variantId]
  );

  const programName = variant?.program?.name;
  const courseFees = variant?.courseFees ?? 0;
  const totalFees =
    variant?.totalFees ?? courseFees - (variant?.discountAllowed ?? 0);
  const choiceName = child?.name;

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="choice-info" className="border-none">
        <AccordionTrigger className="text-b2-mid font-medium hover:no-underline px-0 py-3 justify-start! gap-2 items-center [&>svg]:translate-y-0">
          Deal Info
        </AccordionTrigger>
        <AccordionContent className="px-0 pb-0">
          <div className="flex flex-col gap-2">
            <InfoField
              icon={GraduationCap}
              label="Deal Name"
              value={choiceName}
            />
            <div className="grid grid-cols-2 gap-2">
              {programName && (
                <InfoField
                  icon={GraduationCap}
                  label="Program"
                  value={programName}
                />
              )}
              <InfoField icon={Layers} label="Variant" value={variantName} />
            </div>
            {variant && (
              <div className="grid grid-cols-2 gap-2">
                <InfoField
                  icon={IndianRupee}
                  label="Course Fees"
                  value={`₹${fmt(courseFees)}`}
                />
                <InfoField
                  icon={Wallet}
                  label="Advance Paid"
                  value={`₹${fmt(variant.advancePaid ?? 0)}`}
                />
              </div>
            )}
            {variant && totalFees > 0 && (
              <div className="rounded-xl bg-[#E9FCF5] px-3 py-2.5 flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#267355]">
                  Total Fees
                </span>
                <span className="text-[14px] font-bold text-[#0b4627] tabular-nums">
                  ₹{fmt(totalFees)}
                </span>
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
